/**
 * Lovable Cart Bridge
 * 
 * Handles postMessage communication from embedded Lovable iframes to:
 * 1. Add items to cart via Shopify's native /cart/add.js
 * 2. Open the cart drawer (mini-cart)
 * 3. Send success/error responses back to iframe
 * 
 * Include via: {{ 'lovable-cart-bridge.js' | asset_url | script_tag }}
 */
(function() {
  'use strict';
  
  // Configuration - allowed iframe origins
  var ALLOWED_ORIGINS = [
    'https://ceuzuxsfupfbmssmeivx.lovable.app',
    'https://preview--bluemoon-opal-bedding.lovable.app',
    'https://kampagne.bluemoon.dk',
    'https://bm-bfvip.lovable.app',
    'https://pre-access.bluemoon.dk',
    'http://localhost:5173',
    'http://localhost:8080'
  ];
  
  // Check if origin is allowed (includes wildcard support for *.lovable.app)
  function isAllowedOrigin(origin) {
    // Direct match check
    var directMatch = ALLOWED_ORIGINS.some(function(allowed) {
      return origin.indexOf(allowed.replace('https://', '').replace('http://', '')) !== -1;
    });
    if (directMatch) return true;
    
    // Wildcard support for any *.lovable.app preview URL
    if (/^https:\/\/[a-z0-9-]+\.lovable\.app$/.test(origin)) {
      return true;
    }
    
    // Also allow preview-- URLs
    if (/^https:\/\/preview--[a-z0-9-]+\.lovable\.app$/.test(origin)) {
      return true;
    }
    
    return false;
  }
  
  // Get mini-cart element
  function getMiniCart() {
    return document.querySelector('mini-cart');
  }
  
  // Get sections to render for cart updates
  function getSectionsToRender() {
    var miniCart = getMiniCart();
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map(function(section) { return section.id; });
    }
    // Fallback sections
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }
  
  // Send response back to iframe using event.source directly
  // This avoids cross-origin issues with findSourceIframe()
  function sendResponse(source, origin, type, operationId, error) {
    if (!source) {
      console.warn('[Lovable Cart Bridge] No source to send response to');
      return;
    }
    
    try {
      source.postMessage({
        type: type,
        operationId: operationId,
        error: error || null
      }, origin || '*');
      console.log('[Lovable Cart Bridge] Response sent:', type, 'to', origin);
    } catch (e) {
      console.warn('[Lovable Cart Bridge] Could not send response:', e);
      // Fallback: try with '*' if specific origin failed
      try {
        source.postMessage({
          type: type,
          operationId: operationId,
          error: error || null
        }, '*');
        console.log('[Lovable Cart Bridge] Response sent with fallback origin');
      } catch (e2) {
        console.error('[Lovable Cart Bridge] All response attempts failed:', e2);
      }
    }
  }
  
  // Handle ADD_TO_CART message
  function handleAddToCart(event, data) {
    var operationId = data.operationId;
    var variantId = data.variantId;
    var quantity = data.quantity;
    // Store event.source and event.origin directly to avoid cross-origin iframe lookup issues
    var source = event.source;
    var origin = event.origin;
    
    console.log('[Lovable Cart Bridge] Adding to cart:', { variantId: variantId, quantity: quantity, origin: origin });
    
    var sections = getSectionsToRender();
    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    
    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        sections: sections,
        sections_url: window.location.pathname
      })
    })
    .then(function(response) { return response.json(); })
    .then(function(parsedState) {
      if (parsedState.status === 422) {
        console.error('[Lovable Cart Bridge] Cart error:', parsedState.description);
        sendResponse(source, origin, 'CART_ERROR', operationId, parsedState.description);
        return;
      }
      
      // Success - update cart UI
      var miniCart = getMiniCart();
      if (miniCart && typeof miniCart.renderContents === 'function') {
        miniCart.renderContents(parsedState);
      }
      
      // Dispatch events for other theme components
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: parsedState }
      }));
      
      if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        document.dispatchEvent(new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate, {
          detail: { source: 'lovable-iframe', cart: parsedState }
        }));
      }
      
      console.log('[Lovable Cart Bridge] Successfully added to cart');
      sendResponse(source, origin, 'CART_SUCCESS', operationId);
    })
    .catch(function(error) {
      console.error('[Lovable Cart Bridge] Failed to add to cart:', error);
      sendResponse(source, origin, 'CART_ERROR', operationId, error.message || 'Unknown error');
    });
  }
  
  // Handle ADD_MULTIPLE_TO_CART message
  function handleAddMultipleToCart(event, data) {
    var operationId = data.operationId;
    var items = data.items;
    // Store event.source and event.origin directly to avoid cross-origin iframe lookup issues
    var source = event.source;
    var origin = event.origin;
    
    console.log('[Lovable Cart Bridge] Adding multiple items:', items, 'from origin:', origin);
    
    var sections = getSectionsToRender();
    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    var itemIndex = 0;
    var lastParsedState = null;
    
    function addNextItem() {
      if (itemIndex >= items.length) {
        // All items added, update UI
        if (lastParsedState) {
          var miniCart = getMiniCart();
          if (miniCart && typeof miniCart.renderContents === 'function') {
            miniCart.renderContents(lastParsedState);
          }
        }
        console.log('[Lovable Cart Bridge] Successfully added multiple items');
        sendResponse(source, origin, 'CART_SUCCESS', operationId);
        return;
      }
      
      var item = items[itemIndex];
      
      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: item.id,
          quantity: item.quantity,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
      .then(function(response) { return response.json(); })
      .then(function(parsedState) {
        lastParsedState = parsedState;
        if (parsedState.status === 422) {
          console.error('[Lovable Cart Bridge] Error adding item:', item.id, parsedState.description);
        }
        itemIndex++;
        addNextItem();
      })
      .catch(function(error) {
        console.error('[Lovable Cart Bridge] Error adding item:', item.id, error);
        itemIndex++;
        addNextItem();
      });
    }
    
    addNextItem();
  }
  
  // Handle OPEN_CART_DRAWER message
  function handleOpenCartDrawer() {
    console.log('[Lovable Cart Bridge] Opening cart drawer');
    
    var miniCart = getMiniCart();
    if (miniCart) {
      if (typeof miniCart.open === 'function') {
        miniCart.open();
      } else if (miniCart.drawer && typeof miniCart.drawer.openMenuDrawer === 'function') {
        miniCart.drawer.openMenuDrawer();
      } else {
        // Fallback: click the cart icon
        var cartTrigger = document.querySelector('[data-cart-drawer-trigger], .cart-icon, [href="/cart"]');
        if (cartTrigger) {
          cartTrigger.click();
        }
      }
    }
  }
  
  // Handle CART_UPDATED message
  function handleCartUpdated() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var countElements = document.querySelectorAll('[data-cart-count], .cart-count-bubble');
        countElements.forEach(function(el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.classList.remove('hidden');
          }
        });
      })
      .catch(function(e) {
        console.warn('[Lovable Cart Bridge] Could not update cart count:', e);
      });
  }
  
  // Main message handler
  function handleMessage(event) {
    // Security check
    if (!isAllowedOrigin(event.origin)) {
      console.log('[Lovable Cart Bridge] Blocked message from non-allowed origin:', event.origin);
      return;
    }
    
    var data = event.data;
    if (!data || !data.type || data.source !== 'lovable-landing') {
      return;
    }
    
    console.log('[Lovable Cart Bridge] ✅ Received message:', data.type, 'from:', event.origin, 'operationId:', data.operationId);
    
    switch (data.type) {
      case 'ADD_TO_CART':
        handleAddToCart(event, data);
        break;
        
      case 'ADD_MULTIPLE_TO_CART':
        handleAddMultipleToCart(event, data);
        break;
        
      case 'OPEN_CART_DRAWER':
        handleOpenCartDrawer();
        break;
        
      case 'CART_UPDATED':
        handleCartUpdated();
        break;
        
      case 'NAVIGATE':
        handleNavigate(data);
        break;
    }
  }
  
  // Handle NAVIGATE message - navigate parent window to requested URL
  function handleNavigate(data) {
    console.log('[Lovable Cart Bridge] handleNavigate called with:', data);
    var url = data.url;
    console.log('[Lovable Cart Bridge] Navigation requested to:', url);
    
    // Security: Only allow navigation to bluemoon.dk URLs or relative paths
    if (url && (url.indexOf('bluemoon.dk') !== -1 || url.startsWith('/'))) {
      console.log('[Lovable Cart Bridge] ✅ Navigating now to:', url);
      window.location.href = url;
    } else {
      console.warn('[Lovable Cart Bridge] ❌ Blocked navigation to non-allowed URL:', url);
    }
  }
  
  // Initialize
  window.addEventListener('message', handleMessage);
  console.log('[Lovable Cart Bridge] Initialized - listening for iframe messages');
  
})();
